import { useEffect, useMemo, useState } from "react";

import { useLocation } from "react-router-dom";

import AdminShell from "../components/AdminShell";

import AdminCrudPanel from "../components/AdminCrudPanel";

import { getPlatformAdmin, getPlatformAudit, upsertPlatformTenant, deletePlatformTenant, upsertPlatformOrganization, deletePlatformOrganization } from "../lib/api";

import { panelFromPath } from "../lib/admin";

import { PLATFORM_PANELS, seedPlatformRows } from "../lib/adminCrudConfig";



const FALLBACK_NAV = [

  { id: "overview", label: "Control plane", route: "/admin/platform" },

  { id: "organizations", label: "Organizations", route: "/admin/platform/organizations" },

  { id: "tenants", label: "Tenants", route: "/admin/platform/tenants" },

  { id: "subscriptions", label: "Subscriptions", route: "/admin/platform/subscriptions" },

  { id: "users", label: "Users & admins", route: "/admin/platform/users" },

  { id: "features", label: "Features", route: "/admin/platform/features" },

  { id: "deployment", label: "Deployment", route: "/admin/platform/deployment" },

  { id: "iam", label: "IAM / SSO", route: "/admin/platform/iam" },

  { id: "ai", label: "AI configuration", route: "/admin/platform/ai" },

  { id: "usage", label: "Usage & cost", route: "/admin/platform/usage" },

  { id: "health", label: "System health", route: "/admin/platform/health" },

  { id: "releases", label: "Releases", route: "/admin/platform/releases" },

  { id: "audit", label: "Audit log", route: "/admin/platform/audit" },

];



export default function AdminPlatform({ user, onLogout }) {

  const location = useLocation();

  const panel = panelFromPath(location.pathname, "/admin/platform");

  const panelConfig = PLATFORM_PANELS[panel] || PLATFORM_PANELS.overview;

  const [data, setData] = useState(null);

  const [auditEvents, setAuditEvents] = useState([]);

  const [error, setError] = useState("");



  useEffect(() => {

    getPlatformAdmin()

      .then(({ data: payload }) => setData(payload))

      .catch((err) => setError(err.response?.data?.detail || "Could not load control plane"));

  }, []);



  useEffect(() => {

    if (panel !== "audit") return undefined;

    getPlatformAudit()

      .then(({ data: payload }) => setAuditEvents(payload.events || []))

      .catch(() => setAuditEvents([]));

    return undefined;

  }, [panel]);



  const nav = data?.nav || FALLBACK_NAV;

  const seedRows = useMemo(

    () => seedPlatformRows(panel, data, auditEvents),

    [panel, data, auditEvents],

  );

  const dependencyOptions = useMemo(() => {
    const orgs = data?.catalog?.organizations || [];
    return {
      organization_id: orgs.map((o) => ({ value: o.id, label: `${o.name} (${o.id})` })),
      organization: orgs.map((o) => ({ value: o.name, label: o.name })),
      product: [{ value: "Modernization Advisor", label: "Modernization Advisor" }, { value: "AI-SDLC", label: "AI-SDLC" }],
    };
  }, [data]);

  const persistRow = async (mode, payload) => {
    if (panel === "organizations") {
      if (mode === "delete") {
        await deletePlatformOrganization(payload.id);
      } else {
        await upsertPlatformOrganization({
          id: payload.id,
          name: payload.name,
          status: payload.status || "live",
          products: payload.products,
        });
      }
      const { data: payload2 } = await getPlatformAdmin();
      setData(payload2);
      return;
    }
    if (panel === "tenants") {
      if (mode === "delete") {
        await deletePlatformTenant(payload.code);
        const { data: payload2 } = await getPlatformAdmin();
        setData(payload2);
        return;
      }
      await upsertPlatformTenant({
        code: payload.code,
        name: payload.name,
        organization_id: payload.organization_id || payload.orgId,
        organization: payload.organization,
        product: payload.product || "Modernization Advisor",
        subscription: payload.subscription,
        deployment: payload.deployment || "shared_saas",
        region: payload.region,
        data_residency: payload.data_residency,
        status: payload.status || "DRAFT",
      });
      const { data: payload2 } = await getPlatformAdmin();
      setData(payload2);
    }
  };



  return (

    <AdminShell kind="platform" user={user} nav={nav} onLogout={onLogout}>

      {error && <div className="error-message">{error}</div>}

      {!data && !error && <div className="loading-screen" data-testid="page-wait">Loading control plane…</div>}

      {data && (

        <div className="dashboard" data-testid="platform-panel" data-panel={panel}>

          <div className="intro-row">

            <div>

              <p className="kicker">YUG360-MIND ADMIN PLATFORM · PLATFORM CONSOLE</p>

              <h1>NextProMind <em>control plane</em></h1>

              <p className="intro-copy">

                One portal, two consoles. Manage organizations, tenants, subscriptions and deployment labels.

                Customer application evidence and MDRs stay in the tenant data plane.

              </p>

            </div>

          </div>

          <p className="admin-note" data-testid="control-plane-note">{data.data_plane?.rule}</p>



          {panel === "overview" && (

            <section className="admin-section">

              <p className="kicker">{panelConfig.kicker}</p>

              <h2 className="admin-crud-title">{panelConfig.title}</h2>

              <p className="muted">{panelConfig.detail}</p>

              <div className="metric-grid" style={{ marginTop: 18 }}>

                {[

                  ["Organizations", (data.catalog?.organizations || []).length],

                  ["Tenants", (data.catalog?.organizations || []).reduce((n, o) => n + (o.tenants || []).length, 0)],

                  ["Features", (data.catalog?.features || []).length],

                  ["Directory", (data.catalog?.directory || []).length],

                ].map(([label, value]) => (

                  <div key={label} className="metric"><div><small>{label}</small><strong>{value}</strong></div></div>

                ))}

              </div>

              <p className="muted small-note" style={{ marginTop: 16 }}>

                Select a sidebar tab — each row lists attributes with always-visible <strong>Actions</strong> on the right:
                View, Review, Modify, Delete and other governed actions where applicable.

                Use <strong>Add new …</strong> (top right or empty state) with dependency fields where required.

                Modify, delete, access and move may require review and approval.

              </p>

            </section>

          )}



          {panel !== "overview" && panelConfig.entityLabel != null && (
            <AdminCrudPanel
              panelConfig={panelConfig}
              rows={seedRows}
              dependencyOptions={dependencyOptions}
              onPersist={panel === "tenants" || panel === "organizations" ? persistRow : undefined}
              testId={`platform-crud-${panel}`}
            />
          )}

        </div>
      )}
    </AdminShell>
  );
}

